

































import { defineComponent, onMounted, toRefs, watch } from '@vue/composition-api';
import { storeToRefs } from 'pinia';
import {
  SidebarInnerWrapper,
  SidebarPlacement,
  ChatContent,
  ChatActions,
  ChatHeader,
} from '@getaccept/dsr-shared';
import type { Channel } from 'pusher-js';
import type { ChatParticipant, Message } from '@getaccept/dsr-shared';
import { useChatPublished } from '../composables/chat-published.composable';
import { usePusherStore } from '../../../pusher/store/pusher.store';
import { useParticipantsStore } from '../../store/participants.store';

export default defineComponent({
  name: 'ChatWrapper',
  components: {
    ChatContent,
    ChatActions,
    SidebarInnerWrapper,
    ChatHeader,
  },
  props: {
    recipientId: {
      required: true,
      type: String,
    },
  },
  setup(props) {
    const participantsStore = useParticipantsStore();
    const pusherStore = usePusherStore();
    const {
      chat,
      loadConversationList,
      setDefaultConversation,
      loadConversationDetails,
      addMessageCreatedListener,
      addTypingStatusListener,
      addMessageReadListener,
      addMessageDeletedListener,
      createMessage,
      setMessageReadBy,
      setTypingStatus,
      deleteMessage,
      setReplyMessage,
      useConversationLastReadList,
    } = useChatPublished();
    const { recipients, user } = storeToRefs(participantsStore);
    const { channels } = storeToRefs(pusherStore);

    onMounted(async () => {
      await loadConversationList();
      setDefaultConversation();
      loadConversationDetails();
    });

    watch(
      () => chat.conversations,
      conversations => {
        pusherStore.setConversationChannels(conversations);
      }
    );

    watch(
      () => channels.value.conversations,
      (newChannels: { [key: string]: Channel }, oldChannels: { [key: string]: Channel }) => {
        const newKeys: string[] = Object.keys(newChannels).filter(
          (key: string) => !oldChannels[key]
        );

        newKeys.forEach((key: string) => {
          addMessageCreatedListener(newChannels[key]);
          addTypingStatusListener(newChannels[key], recipients.value, user.value);
          addMessageReadListener(newChannels[key]);
          addMessageDeletedListener(newChannels[key]);
        });
      },
      { deep: true }
    );

    const owner: ChatParticipant = {
      recipientId: props.recipientId,
    };

    const lastReads = useConversationLastReadList(recipients.value, user.value, {
      recipientId: props.recipientId,
    });

    const handleSend = (text: string) => {
      createMessage(text, props.recipientId);
      handleClearReply();
    };

    const handleRead = (messageId: string) => setMessageReadBy(messageId);
    const handleTyping = (isTyping: boolean) => setTypingStatus(isTyping);
    const handleDelete = (messageId: string) => deleteMessage(messageId);

    const handleReply = (message: Message) => {
      setReplyMessage(message);
    };

    const handleClearReply = (): void => {
      setReplyMessage(undefined);
    };

    return {
      ...toRefs(chat),
      recipients,
      user,
      handleSend,
      owner,
      handleRead,
      SidebarPlacement,
      lastReads,
      handleTyping,
      handleDelete,
      handleReply,
      handleClearReply,
    };
  },
});
